import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Breadcrumbs,
  Link as MuiLink,
  CircularProgress,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Search, Target, Users } from 'lucide-react';
import Sidebar from '../components/dashboard/Sidebar';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import { getFullUserData, getUserBusinessName } from '../utils/userHelper';
import { getCampaignById, getCampaignStats, completeCampaign } from '../services/campaignService';
import { getCampaignParticipants } from '../services/contactService';
import type { CampaignData, CampaignStats } from '../types/campaign';
import type { ParticipantData } from '../types/contact';

const PageContainer = styled(Box)({
  display: 'flex',
  minHeight: '100vh',
  bgcolor: '#F9FAFB'
});

const MainContent = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column'
});

const ContentArea = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  maxWidth: '1200px',
  margin: '0 auto',
  width: '100%'
}));

const StatsCard = styled(Card)(() => ({
  border: '1px solid #E5E7EB',
  borderRadius: '12px',
  boxShadow: 'none',
  height: '100%'
}));

const InfoCard = styled(Card)(() => ({
  border: '1px solid #E5E7EB',
  borderRadius: '12px',
  boxShadow: 'none',
  marginBottom: '20px'
}));

const getStatusColor = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'active':
      return 'success';
    case 'draft':
      return 'default';
    case 'completed':
      return 'info';
    case 'paused':
      return 'warning';
    default:
      return 'default';
  }
};


interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

const CampaignDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { campaignId } = useParams<{ campaignId: string }>();
  const userData = getFullUserData();
  const businessName = getUserBusinessName();
  
  const [campaign, setCampaign] = useState<CampaignData | null>(null);
  const [stats, setStats] = useState<CampaignStats | null>(null);
  const [participants, setParticipants] = useState<ParticipantData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingParticipants, setIsLoadingParticipants] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  // Show success message from navigation state
  useEffect(() => {
    if (location.state?.message) {
      setSuccess(location.state.message);
      // Clear the message from location state
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Fetch campaign details and stats
  useEffect(() => {
    let isMounted = true; // Cleanup flag to prevent state updates after unmount

    const fetchCampaignData = async () => {
      if (!campaignId) {
        setError('Campaign ID is missing');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        
        // Fetch both campaign details and stats in parallel
        const [campaignResponse, statsResponse] = await Promise.all([
          getCampaignById(campaignId),
          getCampaignStats(campaignId)
        ]);
        
        // Only update state if component is still mounted
        if (!isMounted) return;
        
        // Handle campaign details response
        if (campaignResponse.success && campaignResponse.data?.campaign) {
          setCampaign(campaignResponse.data.campaign);
          console.log('✅ Campaign details loaded:', campaignResponse.data.campaign.campaign_name);
          console.log('📋 Survey questions:', campaignResponse.data.campaign.survey?.questions?.length || 0, 'questions');
          if (campaignResponse.data.campaign.survey?.questions) {
            console.log('Questions:', campaignResponse.data.campaign.survey.questions);
          }
        } else {
          throw new Error(campaignResponse.message || 'Failed to fetch campaign details');
        }

        // Handle campaign stats response
        if (statsResponse.success && statsResponse.data?.stats) {
          setStats(statsResponse.data.stats);
          console.log('✅ Campaign stats loaded');
        } else {
          console.warn('⚠️ Campaign stats not available:', statsResponse.message);
          // Don't throw error for stats, just log warning
        }
      } catch (err) {
        if (!isMounted) return; // Don't set error if unmounted
        console.error('❌ Error fetching campaign data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load campaign data');
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchCampaignData();

    // Cleanup function to prevent memory leaks
    return () => {
      isMounted = false;
    };
  }, [campaignId]);

  // Fetch participants when Participants tab is selected
  useEffect(() => {
    let isMounted = true; // Cleanup flag

    const fetchParticipants = async () => {
      if (!campaignId || tabValue !== 1) {
        return; // Only fetch when Participants tab is active
      }

      try {
        setIsLoadingParticipants(true);
        
        const response = await getCampaignParticipants(campaignId, 50, 0);
        
        // Only update state if component is still mounted
        if (!isMounted) return;
        
        if (response.success && response.data?.contacts) {
          setParticipants(response.data.contacts);
          console.log('✅ Participants loaded:', response.data.contacts.length);
        } else {
          console.warn('⚠️ No participants data available. Response:', response);
        }
      } catch (err) {
        if (!isMounted) return;
        console.error('❌ Error fetching participants:', err);
        setError(err instanceof Error ? err.message : 'Failed to load participants');
      } finally {
        if (isMounted) {
          setIsLoadingParticipants(false);
        }
      }
    };

    fetchParticipants();

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [campaignId, tabValue]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleEditCampaign = () => {
    // Navigate to edit page with campaign data
    navigate('/campaigns/create', {
      state: {
        isEditMode: true,
        campaignId: campaignId,
        campaignData: campaign
      }
    });
  };

  const handleEditSurvey = () => {
    // Navigate to survey creation page
    navigate('/campaigns/create/survey', {
      state: {
        campaignId: campaignId,
        campaignData: campaign,
        isEditMode: true
      }
    });
  };

  const handleEditCustomers = () => {
    // Navigate to customer upload page
    navigate('/campaigns/create/customers', {
      state: {
        campaignId: campaignId,
        campaignData: campaign,
        isEditMode: true
      }
    });
  };

  const handleEndCampaign = async () => {
    if (!campaignId) {
      setError('Campaign ID is missing');
      return;
    }

    const isDraft = campaign?.status?.toLowerCase() === 'draft';
    const action = isDraft ? 'cancel' : 'end';
    
    const confirmMessage = isDraft 
      ? 'Are you sure you want to cancel this draft campaign? This action cannot be undone.'
      : 'Are you sure you want to end this campaign? This will stop all active invitations and mark the campaign as completed.';
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      console.log(`${action}ing campaign:`, campaignId);
      
      // Call the complete/end campaign API
      const response = await completeCampaign(campaignId);
      
      if (response.success) {
        console.log(`Campaign ${action}ed successfully`);
        
        // Navigate back to campaigns list immediately
        navigate('/campaigns', {
          state: {
            message: `Campaign ${action}ed successfully!`
          }
        });
      } else {
        throw new Error(response.message || `Failed to ${action} campaign`);
      }
    } catch (err) {
      console.error(`Failed to ${action} campaign:`, err);
      setError(err instanceof Error ? err.message : `Failed to ${action} campaign. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <PageContainer>
        <Sidebar user={userData} />
        <MainContent>
          <DashboardHeader
            businessName={businessName}
            userAvatar={userData.avatar}
            businessLogo={userData.businessLogo}
          />
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <CircularProgress size={40} />
          </Box>
        </MainContent>
      </PageContainer>
    );
  }

  if (error || !campaign) {
    return (
      <PageContainer>
        <Sidebar user={userData} />
        <MainContent>
          <DashboardHeader
            businessName={businessName}
            userAvatar={userData.avatar}
            businessLogo={userData.businessLogo}
          />
          <ContentArea>
            <Alert severity="error">{error || 'Campaign not found'}</Alert>
            <Button onClick={() => navigate('/campaigns')} sx={{ mt: 2 }}>
              Back to Campaigns
            </Button>
          </ContentArea>
        </MainContent>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Sidebar user={userData} />
      
      <MainContent>
        <DashboardHeader
          businessName={businessName}
          userAvatar={userData.avatar}
          businessLogo={userData.businessLogo}
        />
        
        <ContentArea>
          {/* Breadcrumbs */}
          <Breadcrumbs sx={{ mb: 2 }}>
            <MuiLink
              component="button"
              onClick={() => navigate('/campaigns')}
              sx={{
                color: '#4F46E5',
                textDecoration: 'none',
                fontSize: '0.875rem',
                fontFamily: '"Inter", sans-serif',
                '&:hover': {
                  textDecoration: 'underline',
                  color: '#4338CA'
                }
              }}
            >
              Campaigns
            </MuiLink>
            <Typography sx={{ fontSize: '0.875rem', fontFamily: '"Inter", sans-serif', color: '#6B7280' }}>
              {campaign.campaign_name}
            </Typography>
          </Breadcrumbs>

          {/* Page Heading & Actions */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontWeight: 700,
                  fontSize: { xs: '1.5rem', md: '1.875rem' },
                  lineHeight: 1.2,
                  letterSpacing: '-0.02em',
                  color: '#111827'
                }}
              >
                {campaign.campaign_name}
              </Typography>
              <Chip
                label={campaign.status || 'Active'}
                color={getStatusColor(campaign.status || 'active') as any}
                size="small"
                sx={{ textTransform: 'capitalize', fontWeight: 600, height: 24 }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button
                variant="outlined"
                onClick={handleEndCampaign}
                disabled={isLoading}
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  height: 40,
                  px: 3,
                  color: '#6B7280',
                  borderColor: '#E5E7EB',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  borderRadius: '8px',
                  '&:hover': {
                    borderColor: '#D1D5DB',
                    bgcolor: '#F9FAFB'
                  }
                }}
              >
                {campaign?.status?.toLowerCase() === 'draft' ? 'Cancel' : 'End Campaign'}
              </Button>
              {campaign?.status?.toLowerCase() === 'draft' && (
                <Button
                  variant="contained"
                  onClick={handleEditCampaign}
                  disabled={isLoading}
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    height: 40,
                    px: 3,
                    backgroundColor: '#4F46E5',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    textTransform: 'none',
                    borderRadius: '8px',
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: '#4338CA',
                      boxShadow: 'none'
                    }
                  }}
                >
                  Edit Campaign
                </Button>
              )}
            </Box>
          </Box>

          {/* Stats Cards */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
              gap: 2,
              mb: 3
            }}
          >
            <StatsCard>
              <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Typography
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    color: '#6B7280',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    mb: 0.5
                  }}
                >
                  Total Participants
                </Typography>
                <Typography
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontWeight: 700,
                    fontSize: '1.5rem',
                    color: '#111827',
                    lineHeight: 1.2
                  }}
                >
                  {stats?.responded_count || 0} / {stats?.total_contacts || 0}
                </Typography>
              </CardContent>
            </StatsCard>

            <StatsCard>
              <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Typography
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    color: '#6B7280',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    mb: 0.5
                  }}
                >
                  Surveys Completed
                </Typography>
                <Typography
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontWeight: 700,
                    fontSize: '1.5rem',
                    color: '#111827',
                    lineHeight: 1.2
                  }}
                >
                  {stats?.responded_count || 0}
                </Typography>
              </CardContent>
            </StatsCard>

            <StatsCard>
              <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Typography
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    color: '#6B7280',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    mb: 0.5
                  }}
                >
                  Surveys Opened
                </Typography>
                <Typography
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontWeight: 700,
                    fontSize: '1.5rem',
                    color: '#111827',
                    lineHeight: 1.2
                  }}
                >
                  {stats?.opened_count || 0}
                </Typography>
              </CardContent>
            </StatsCard>

            <StatsCard>
              <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Typography
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    color: '#6B7280',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    mb: 0.5
                  }}
                >
                  Response Rate
                </Typography>
                <Typography
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontWeight: 700,
                    fontSize: '1.5rem',
                    color: '#111827',
                    lineHeight: 1.2
                  }}
                >
                  {stats?.response_rate ? `${stats.response_rate.toFixed(1)}%` : '0%'}
                </Typography>
              </CardContent>
            </StatsCard>
          </Box>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: '#E5E7EB', mb: 2 }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              sx={{
                minHeight: 44,
                '& .MuiTab-root': {
                  fontFamily: '"Inter", sans-serif',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  minHeight: 44,
                  color: '#6B7280',
                  '&.Mui-selected': {
                    color: '#4F46E5'
                  }
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#4F46E5'
                }
              }}
            >
              <Tab label="Configuration" />
              <Tab label="Participants" />
            </Tabs>
          </Box>

          {/* Tab Content */}
          <TabPanel value={tabValue} index={0}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' },
                gap: 3
              }}
            >
              {/* Left Column */}
              <Box>
                {/* Survey Questions */}
                <InfoCard>
                  <CardContent sx={{ p: 2.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography
                        sx={{
                          fontFamily: '"Inter", sans-serif',
                          fontWeight: 700,
                          fontSize: '1rem',
                          color: '#111827'
                        }}
                      >
                        Survey Questions
                      </Typography>
                      {campaign?.status?.toLowerCase() === 'draft' && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleEditSurvey}
                          sx={{
                            fontFamily: '"Inter", sans-serif',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            borderColor: '#4F46E5',
                            color: '#4F46E5',
                            borderRadius: '6px',
                            px: 2,
                            py: 0.5,
                            '&:hover': {
                              borderColor: '#4338CA',
                              backgroundColor: '#EEF2FF'
                            }
                          }}
                        >
                          {campaign.survey?.questions && campaign.survey.questions.length > 0 ? 'Edit Survey' : 'Create Survey'}
                        </Button>
                      )}
                    </Box>
                    {campaign.survey?.questions && campaign.survey.questions.length > 0 ? (
                      <Stack spacing={1.5}>
                        {campaign.survey.questions
                          .sort((a, b) => a.question_order - b.question_order)
                          .map((question, index) => (
                            <Box key={question.id} sx={{ display: 'flex', gap: 1.5 }}>
                              <Typography sx={{ color: '#4F46E5', fontWeight: 700, minWidth: '20px', fontFamily: '"Inter", sans-serif', fontSize: '0.875rem' }}>
                                {index + 1}.
                              </Typography>
                              <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.875rem', color: '#111827', mb: 0.5 }}>
                                  {question.question_text}
                                  {question.is_mandatory && (
                                    <Typography component="span" sx={{ color: '#EF4444', ml: 0.5 }}>
                                      *
                                    </Typography>
                                  )}
                                </Typography>
                                <Typography 
                                  sx={{ 
                                    fontFamily: '"Inter", sans-serif',
                                    color: '#9CA3AF', 
                                    fontSize: '0.75rem',
                                    textTransform: 'capitalize'
                                  }}
                                >
                                  {question.answer_type.replace('_', ' ')}
                                  {question.options && question.options.length > 0 && (
                                    <Typography 
                                      component="span" 
                                      sx={{ 
                                        ml: 1,
                                        color: '#6B7280'
                                      }}
                                    >
                                      • {question.options.join(', ')}
                                    </Typography>
                                  )}
                                </Typography>
                              </Box>
                            </Box>
                          ))}
                      </Stack>
                    ) : (
                      <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.875rem', color: '#9CA3AF', fontStyle: 'italic' }}>
                        No survey questions available
                      </Typography>
                    )}
                  </CardContent>
                </InfoCard>
              </Box>

              {/* Right Column - Sidebar */}
              <Box>
                {/* Reward Structure */}
                <InfoCard>
                  <CardContent sx={{ p: 2.5 }}>
                    <Typography
                      sx={{
                        fontFamily: '"Inter", sans-serif',
                        fontWeight: 700,
                        fontSize: '1rem',
                        color: '#111827',
                        mb: 2
                      }}
                    >
                      Reward Structure
                    </Typography>
                    <Stack spacing={1.5}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.875rem', color: '#6B7280' }}>
                          Reward Type
                        </Typography>
                        <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.875rem', fontWeight: 600, color: '#111827', textTransform: 'capitalize' }}>
                          {campaign.reward_type}
                        </Typography>
                      </Box>
                      {campaign.reward_type === 'coupon' && campaign.coupon_discount_percent && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.875rem', color: '#6B7280' }}>
                            Discount
                          </Typography>
                          <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.875rem', fontWeight: 600, color: '#111827' }}>
                            {campaign.coupon_discount_percent}%
                          </Typography>
                        </Box>
                      )}
                      {campaign.reward_type === 'cash' && (
                        <>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.875rem', color: '#6B7280' }}>
                              Amount
                            </Typography>
                            <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.875rem', fontWeight: 600, color: '#111827' }}>
                              ${campaign.cash_amount} {campaign.cash_currency}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.875rem', color: '#6B7280' }}>
                              Payment Method
                            </Typography>
                            <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.875rem', fontWeight: 600, color: '#111827', textTransform: 'capitalize' }}>
                              {campaign.payment_integrator}
                            </Typography>
                          </Box>
                        </>
                      )}
                    </Stack>
                  </CardContent>
                </InfoCard>

                {/* Target Platforms */}
                <InfoCard>
                  <CardContent sx={{ p: 2.5 }}>
                    <Typography
                      sx={{
                        fontFamily: '"Inter", sans-serif',
                        fontWeight: 700,
                        fontSize: '1rem',
                        color: '#111827',
                        mb: 2
                      }}
                    >
                      Target Platform
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '8px',
                          backgroundColor: '#F9FAFB',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Target size={20} color="#4F46E5" />
                      </Box>
                      <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.875rem', fontWeight: 600, textTransform: 'capitalize' }}>
                        {campaign.platform}
                      </Typography>
                    </Box>
                  </CardContent>
                </InfoCard>

                {/* General Info */}
                <InfoCard>
                  <CardContent sx={{ p: 2.5 }}>
                    <Typography
                      sx={{
                        fontFamily: '"Inter", sans-serif',
                        fontWeight: 700,
                        fontSize: '1rem',
                        color: '#111827',
                        mb: 2
                      }}
                    >
                      General Info
                    </Typography>
                    <Stack spacing={1.5}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.875rem', color: '#6B7280' }}>
                          Created Date
                        </Typography>
                        <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.875rem', fontWeight: 600, color: '#111827' }}>
                          {campaign.created_at
                            ? new Date(campaign.created_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })
                            : 'N/A'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.875rem', color: '#6B7280' }}>
                          Platform Link
                        </Typography>
                        <MuiLink
                          href={campaign.platform_link}
                          target="_blank"
                          sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.875rem', fontWeight: 600, color: '#4F46E5', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                        >
                          View
                        </MuiLink>
                      </Box>
                    </Stack>
                  </CardContent>
                </InfoCard>
              </Box>
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {isLoadingParticipants ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={40} />
              </Box>
            ) : participants.length > 0 ? (
              <Box>
                {/* Section Header */}
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between', 
                  alignItems: { xs: 'flex-start', sm: 'center' }, 
                  mb: 2.5,
                  gap: 2
                }}>
                  <Box sx={{ display: 'flex', gap: 2, flex: 1, width: '100%' }}>
                    <TextField
                      placeholder="Search participants..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      size="small"
                    sx={{ 
                      width: { xs: '100%', sm: 300 },
                      '& .MuiOutlinedInput-root': {
                        fontFamily: '"Inter", sans-serif',
                        bgcolor: '#F9FAFB',
                        '& fieldset': {
                          borderColor: '#E5E7EB',
                        },
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search size={18} color="#9CA3AF" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  </Box>
                  {campaign?.status?.toLowerCase() === 'draft' && (
                    <Button
                      variant="contained"
                      onClick={handleEditCustomers}
                      sx={{
                        fontFamily: '"Inter", sans-serif',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        backgroundColor: '#4F46E5',
                        borderRadius: '8px',
                        px: 3,
                        py: 1,
                        whiteSpace: 'nowrap',
                        '&:hover': {
                          backgroundColor: '#4338CA'
                        }
                      }}
                    >
                      Upload More Contacts
                    </Button>
                  )}
                </Box>

                {/* Participants Table */}
                <Card sx={{ 
                  border: '1px solid #E5E7EB', 
                  borderRadius: '12px',
                  boxShadow: 'none',
                  overflow: 'hidden'
                }}>
                  <TableContainer>
                    <Table>
                      <TableHead sx={{ bgcolor: '#F9FAFB' }}>
                        <TableRow>
                          <TableCell sx={{ 
                            fontFamily: '"Inter", sans-serif',
                            fontWeight: 600, 
                            fontSize: '0.75rem', 
                            textTransform: 'uppercase', 
                            color: '#6B7280',
                            letterSpacing: '0.05em',
                            py: 1.5
                          }}>
                            Participant
                          </TableCell>
                          <TableCell sx={{ 
                            fontFamily: '"Inter", sans-serif',
                            fontWeight: 600, 
                            fontSize: '0.75rem', 
                            textTransform: 'uppercase', 
                            color: '#6B7280',
                            letterSpacing: '0.05em',
                            py: 1.5
                          }}>
                            Joined
                          </TableCell>
                          <TableCell sx={{ 
                            fontFamily: '"Inter", sans-serif',
                            fontWeight: 600, 
                            fontSize: '0.75rem', 
                            textTransform: 'uppercase', 
                            color: '#6B7280',
                            letterSpacing: '0.05em',
                            py: 1.5
                          }}>
                            Survey
                          </TableCell>
                          <TableCell sx={{ 
                            fontFamily: '"Inter", sans-serif',
                            fontWeight: 600, 
                            fontSize: '0.75rem', 
                            textTransform: 'uppercase', 
                            color: '#6B7280',
                            letterSpacing: '0.05em',
                            py: 1.5
                          }}>
                            Review
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {participants
                          .filter(p => 
                            !searchQuery || 
                            p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            p.email?.toLowerCase().includes(searchQuery.toLowerCase())
                          )
                          .map((participant) => {
                            // Helper function to get status colors
                            const getSurveyStatusStyle = (status?: string) => {
                              switch (status?.toLowerCase()) {
                                case 'completed':
                                  return { bg: 'rgba(46, 125, 50, 0.1)', color: '#2ECC71', dot: '#2ECC71' };
                                case 'pending':
                                  return { bg: 'rgba(243, 156, 18, 0.1)', color: '#F39C12', dot: '#F39C12' };
                                case 'disqualified':
                                  return { bg: 'rgba(231, 76, 60, 0.1)', color: '#E74C3C', dot: '#E74C3C' };
                                case 'invited':
                                default:
                                  return { bg: 'rgba(107, 114, 128, 0.1)', color: '#6B7280', dot: '#6B7280' };
                              }
                            };

                            const getReviewStatusStyle = (status?: string) => {
                              switch (status?.toLowerCase()) {
                                case 'submitted':
                                  return { bg: 'rgba(46, 125, 50, 0.1)', color: '#2ECC71', dot: '#2ECC71' };
                                case 'pending':
                                  return { bg: 'rgba(243, 156, 18, 0.1)', color: '#F39C12', dot: '#F39C12' };
                                case 'not_started':
                                case 'n/a':
                                default:
                                  return { bg: 'rgba(107, 114, 128, 0.1)', color: '#6B7280', dot: '#6B7280' };
                              }
                            };

                            const surveyStyle = getSurveyStatusStyle(participant.survey_status || 'pending');
                            const reviewStyle = getReviewStatusStyle(participant.review_status || 'not_started');

                              return (
                              <TableRow 
                                key={participant.id} 
                                sx={{ 
                                  '&:hover': { bgcolor: '#F9FAFB' },
                                  borderBottom: '1px solid #F3F4F6'
                                }}
                              >
                                <TableCell sx={{ py: 2 }}>
                                  <Box>
                                    <Typography sx={{ fontFamily: '"Inter", sans-serif', fontWeight: 500, fontSize: '0.875rem', color: '#111827' }}>
                                      {participant.name}
                                    </Typography>
                                    <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.75rem', color: '#9CA3AF' }}>
                                      {participant.email || participant.phone || 'N/A'}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ py: 2 }}>
                                  <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.875rem', color: '#6B7280' }}>
                                    {new Date(participant.joined_at || participant.created_at).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric'
                                    })}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ py: 2 }}>
                                  <Chip
                                    label={participant.survey_status || 'Pending'}
                                    size="small"
                                    sx={{
                                      bgcolor: surveyStyle.bg,
                                      color: surveyStyle.color,
                                      fontFamily: '"Inter", sans-serif',
                                      fontSize: '0.75rem',
                                      fontWeight: 500,
                                      height: 24,
                                      textTransform: 'capitalize'
                                    }}
                                  />
                                </TableCell>
                                <TableCell sx={{ py: 2 }}>
                                  <Chip
                                    label={participant.review_status === 'n/a' ? 'N/A' : (participant.review_status || 'Not Started').replace('_', ' ')}
                                    size="small"
                                    sx={{
                                      bgcolor: reviewStyle.bg,
                                      color: reviewStyle.color,
                                      fontFamily: '"Inter", sans-serif',
                                      fontSize: '0.75rem',
                                      fontWeight: 500,
                                      height: 24,
                                      textTransform: 'capitalize'
                                    }}
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Card>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Users size={48} color="#CBD5E1" />
                <Typography sx={{ fontFamily: '"Inter", sans-serif', fontWeight: 600, fontSize: '1rem', mt: 2, color: '#6B7280' }}>
                  No participants yet
                </Typography>
                <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.875rem', color: '#9CA3AF', mt: 0.5, mb: 3 }}>
                  Upload contacts to invite participants to this campaign
                </Typography>
                {campaign?.status?.toLowerCase() === 'draft' && (
                  <Button
                    variant="contained"
                    onClick={handleEditCustomers}
                    sx={{
                      fontFamily: '"Inter", sans-serif',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      backgroundColor: '#4F46E5',
                      borderRadius: '8px',
                      px: 3,
                      py: 1,
                      '&:hover': {
                        backgroundColor: '#4338CA'
                      }
                    }}
                  >
                    Upload Contacts
                  </Button>
                )}
              </Box>
            )}
          </TabPanel>

          {/* Error Snackbar */}
          <Snackbar
            open={!!error}
            autoHideDuration={6000}
            onClose={() => setError(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
              {error}
            </Alert>
          </Snackbar>

          {/* Success Snackbar */}
          <Snackbar
            open={!!success}
            autoHideDuration={3000}
            onClose={() => setSuccess(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
              {success}
            </Alert>
          </Snackbar>
        </ContentArea>
      </MainContent>
    </PageContainer>
  );
};

export default CampaignDetails;
